import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatCurrency } from "@/utils/common";
import { calculatePpf } from "../../../shared-utils/ppfCalculatorCore.mjs";

/**
 * React component to calculate and display Public Provident Fund (PPF) returns.
 * @example
 * PPFCalculator()
 * <Component ... />
 * @returns {JSX.Element} Returns a card containing sliders, inputs, and results based on user inputs.
 * @description
 *   - Utilizes `useState` to track changes in investment parameters.
 *   - Leverages `useEffect` to compute PPF results whenever input values change.
 *   - Includes example usage of formatCurrency method for styling currency.
 *   - Provides user's ability to change investment frequency and the initial investment values through sliders and inputs.
 */
const PPFCalculator: React.FC = () => {
  const [yearlyInvestment, setYearlyInvestment] = useState<number>(150000);
  const [interestRate, setInterestRate] = useState<number>(7.1);
  const [years, setYears] = useState<number>(15);
  const [investmentFrequency, setInvestmentFrequency] = useState<"yearly" | "monthly">("yearly");
  const [results, setResults] = useState({
    totalInvestment: 0,
    totalInterest: 0,
    maturityAmount: 0,
  });

  useEffect(() => {
    const calculatedResults = calculatePpf(
      yearlyInvestment,
      interestRate,
      years,
      investmentFrequency
    );

    setResults(calculatedResults);
  }, [yearlyInvestment, interestRate, years, investmentFrequency]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>PPF Calculator</CardTitle>
        <CardDescription>
          Calculate your Public Provident Fund (PPF) returns over time
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <Label>Investment Frequency</Label>
          <RadioGroup
            value={investmentFrequency}
            onValueChange={(value) => setInvestmentFrequency(value as "yearly" | "monthly")}
            className="flex space-x-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yearly" id="yearly" />
              <Label htmlFor="yearly">Yearly</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="monthly" id="monthly" />
              <Label htmlFor="monthly">Monthly</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="yearly-investment">Yearly Investment (₹)</Label>
            <div className="font-medium">{formatCurrency(yearlyInvestment)}</div>
          </div>
          <Slider
            id="yearly-investment"
            min={500}
            max={150000}
            step={500}
            value={[yearlyInvestment]}
            onValueChange={(value) => setYearlyInvestment(value[0])}
          />
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              value={yearlyInvestment}
              onChange={(e) => setYearlyInvestment(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="text-xs text-gray-500">
            {investmentFrequency === "monthly"
              ? `${formatCurrency(yearlyInvestment / 12)} per month`
              : "Lump sum yearly investment"}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="interest-rate">Interest Rate (%)</Label>
            <div className="font-medium">{interestRate}%</div>
          </div>
          <Slider
            id="interest-rate"
            min={5}
            max={10}
            step={0.1}
            value={[interestRate]}
            onValueChange={(value) => setInterestRate(value[0])}
          />
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="w-full"
              step="0.1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="years">Time Period (Years)</Label>
            <div className="font-medium">{years} years</div>
          </div>
          <Slider
            id="years"
            min={15}
            max={50}
            step={1}
            value={[years]}
            onValueChange={(value) => setYears(value[0])}
          />
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="text-xs text-gray-500">Minimum PPF lock-in period is 15 years</div>
        </div>

        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Total Investment</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.totalInvestment)}
              </div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Total Interest</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.totalInterest)}
              </div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Maturity Amount</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.maturityAmount)}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between border-t border-gray-200 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setYearlyInvestment(150000);
            setInterestRate(7.1);
            setYears(15);
            setInvestmentFrequency("yearly");
          }}
        >
          Reset
        </Button>
        <Button variant="default">Calculate</Button>
      </CardFooter>
    </Card>
  );
};

export default PPFCalculator;
